import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
// import { Onboarding } from './components/Onboarding'; // Lazy loaded instead
import { User, Precedent } from './types';
import { isSuperAdminEmail } from './lib/constants';
import { fetchWithAuth } from './lib/api';
import { AlertTriangle } from 'lucide-react';

function lazyWithRetry(importFn: () => Promise<any>, exportName: string): any {
  return React.lazy(async () => {
    try {
      const module = await importFn();
      return { default: module[exportName] || module.default };
    } catch (error) {
      console.warn(`[Chunk Load Warning] Module ${exportName} failed to load, reloading app...`, error);
      window.location.reload();
      return { default: () => null as any };
    }
  });
}

const DashboardView = lazyWithRetry(() => import('./components/views/DashboardView'), 'DashboardView');
const SearchView = lazyWithRetry(() => import('./components/views/SearchView'), 'SearchView');
const CaseReportView = lazyWithRetry(() => import('./components/views/CaseReportView'), 'CaseReportView');
const AiAssistantView = lazyWithRetry(() => import('./components/views/AiAssistantView'), 'AiAssistantView');
const AdminDashboardView = lazyWithRetry(() => import('./components/views/AdminDashboardView'), 'AdminDashboardView');
const AdvocatesView = lazyWithRetry(() => import('./components/views/AdvocatesView'), 'AdvocatesView');
const AdvocateMarketplaceView = lazyWithRetry(() => import('./components/views/AdvocateMarketplaceView'), 'AdvocateMarketplaceView');
const AdvocateDashboardView = lazyWithRetry(() => import('./components/views/AdvocateDashboardView'), 'AdvocateDashboardView');
const LoginView = lazyWithRetry(() => import('./components/views/LoginView'), 'LoginView');
const WorkspaceView = lazyWithRetry(() => import('./components/views/WorkspaceView'), 'WorkspaceView');
const RepositoryView = lazyWithRetry(() => import('./components/views/RepositoryView'), 'RepositoryView');
const ClientPortalView = lazyWithRetry(() => import('./components/views/ClientPortalView'), 'ClientPortalView');
const MyCasesView = lazyWithRetry(() => import('./components/views/MyCasesView'), 'MyCasesView');
const AboutMeView = lazyWithRetry(() => import('./components/views/AboutMeView'), 'AboutMeView');
const PrivacyPolicyView = lazyWithRetry(() => import('./components/views/PrivacyPolicyView'), 'PrivacyPolicyView');
const TermsOfServiceView = lazyWithRetry(() => import('./components/views/TermsOfServiceView'), 'TermsOfServiceView');
const RefundCancellationPolicyView = lazyWithRetry(() => import('./components/views/RefundCancellationPolicyView'), 'RefundCancellationPolicyView');
const PublicLawsView = lazyWithRetry(() => import('./components/views/PublicLawsView'), 'PublicLawsView');
const LawDetailView: any = lazyWithRetry(() => import('./components/views/LawDetailView'), 'LawDetailView');
import { LandingView } from './components/views/LandingView';
const BlogsView = lazyWithRetry(() => import('./components/views/BlogsView'), 'BlogsView');
const BlogDetailView = lazyWithRetry(() => import('./components/views/BlogDetailView'), 'BlogDetailView');
const TopicHubView = lazyWithRetry(() => import('./components/views/TopicHubView'), 'TopicHubView');
const LegalDisclaimerView = lazyWithRetry(() => import('./components/views/LegalDisclaimerView'), 'LegalDisclaimerView');
const ContactView = lazyWithRetry(() => import('./components/views/ContactView'), 'ContactView');
const Onboarding = lazyWithRetry(() => import('./components/Onboarding'), 'Onboarding');

import { useParams } from 'react-router-dom';

function CaseReportPage({ user, bookmarkedIds, handleToggleBookmark, handleNavigate }: any) {
  const { id } = useParams();
  const [precedent, setPrecedent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadPrecedent() {
      try {
        const data = await fetchWithAuth(`/api/precedents/${id}`);
        if (data && !data.error && (data.id || data.title || data.citation)) {
          setPrecedent(data);
          setError('');
          return;
        }
        throw new Error(data?.error || 'Precedent API returned empty');
      } catch (err: any) {
        console.warn(`[CaseReportPage] API fetch failed for ${id}:`, err.message);
        setError(err.message || 'Failed to load precedent');
      } finally {
        setLoading(false);
      }
    }
    if (id) loadPrecedent();
  }, [id]);

  if (loading) return <div className="flex h-full items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div></div>;
  if (error || !precedent) return <div className="flex h-full items-center justify-center flex-col gap-4 text-slate-500"><AlertTriangle className="w-12 h-12 text-red-400" /><p>{error || 'Precedent not found'}</p><button onClick={() => handleNavigate('search')} className="text-emerald-600 hover:underline">Back to Search</button></div>;

  return (
    <CaseReportView 
      precedent={precedent} 
      onBack={() => handleNavigate('search')} 
      user={user} 
      bookmarkedIds={bookmarkedIds} 
      onToggleBookmark={handleToggleBookmark} 
      onNavigate={handleNavigate} 
    />
  );
}

function LawDetailPage({ handleNavigate }: any) {
  const { id } = useParams();
  return <LawDetailView lawId={id!} onBack={() => handleNavigate('laws')} />;
}

function TopicHubPage({ handleNavigate, handleViewCase }: any) {
  const { slug } = useParams<{ slug: string }>();
  return (
    <TopicHubView
      slug={slug || ''}
      onNavigate={handleNavigate}
      onViewCase={handleViewCase}
      onViewLaw={(id: string) => handleNavigate('laws', `/laws/${id}`)}
      onViewBlog={(id: string, bSlug?: string) => handleNavigate('blogs', `/blogs/${bSlug || id}`)}
    />
  );
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(() => {
    try {
      const saved = localStorage.getItem('paklegal_user_session');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());
  const [authInitialized, setAuthInitialized] = useState(false);

  // Idle preloading for non-active route bundles to make page navigation instant (0ms)
  useEffect(() => {
    const preloadRoutes = () => {
      import('./components/views/PublicLawsView');
      import('./components/views/TermsOfServiceView');
      import('./components/views/PrivacyPolicyView');
      import('./components/views/RefundCancellationPolicyView');
      import('./components/views/AboutMeView');
      import('./components/views/AdvocateMarketplaceView');
      import('./components/views/BlogsView');
      import('./components/views/AiAssistantView');
    };
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => setTimeout(preloadRoutes, 15000));
    } else {
      setTimeout(preloadRoutes, 15000);
    }
  }, []);

  useEffect(() => {
    let unsubscribe: () => void = () => {};
    let mounted = true;

    const initAuth = async () => {
      try {
        const { auth, db } = await import('./firebase');
        const { onAuthStateChanged } = await import('firebase/auth');
        const { doc, getDoc } = await import('firebase/firestore');

        if (!mounted) return;

        unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser) {
            try {
              const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
              let loggedInUser: User;
              const firebaseEmail = (firebaseUser.email || '').toLowerCase();
              const isKnownAdmin = isSuperAdminEmail(firebaseEmail);

              if (userDoc.exists()) {
                const data = userDoc.data();
                loggedInUser = {
                  id: firebaseUser.uid,
                  name: data.name || firebaseUser.displayName || 'Google User',
                  email: firebaseUser.email || firebaseUser.uid,
                  role: isKnownAdmin ? 'Super Admin' : (data.role || 'Lawyer'),
                  status: data.status || 'Active',
                  isPremium: data.isPremium || false,
                  plan: data.plan,
                  interests: data.interests || []
                };
              } else {
                loggedInUser = {
                  id: firebaseUser.uid,
                  name: firebaseUser.displayName || 'Google User',
                  email: firebaseUser.email || firebaseUser.uid,
                  role: isKnownAdmin ? 'Super Admin' : 'Lawyer',
                  status: 'Active',
                  isPremium: false
                };
              }
              if (mounted) {
                setUser(loggedInUser);
                localStorage.setItem('paklegal_user_session', JSON.stringify(loggedInUser));

                // Auto-redirect admin to /admin dashboard only on initial login/launch if on /login or /
                const currentPath = window.location.pathname;
                if (isKnownAdmin && (currentPath === '/login' || currentPath === '/')) {
                  navigate('/admin', { replace: true });
                }
              }
            } catch (e) {
              console.warn('Failed to sync authenticated user profile (falling back to credentials/cache):', e);
            } finally {
              if (mounted) setAuthInitialized(true);
            }
          } else {
            if (mounted) {
              setUser(null);
              localStorage.removeItem('paklegal_user_session');
              setAuthInitialized(true);
            }
          }
        });
      } catch (err) {
        console.warn('Failed to dynamically init auth', err);
        if (mounted) setAuthInitialized(true);
      }
    };

    // Delay Firebase initialization to prioritize main thread for rendering LCP
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => setTimeout(initAuth, 2000));
    } else {
      setTimeout(initAuth, 2500);
    }

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // Background datasets are fetched on-demand per view (SearchView, PublicLawsView, etc.)
  // to avoid downloading 4.8MB of JSON payloads on initial landing page load.

  // Run the background publisher queue
  useEffect(() => {
    if (authInitialized && user?.role === 'Super Admin') {
      import('./services/dataService').then(({ runQueuePublisher }) => {
        runQueuePublisher();
      });
    }
  }, [authInitialized, user?.role]);

  const handleToggleBookmark = (id: string) => {
    setBookmarkedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleUpdateUser = (updatedFields: Partial<User>) => {
    setUser(prev => {
      if (!prev) return null;
      const next = { ...prev, ...updatedFields };
      localStorage.setItem('paklegal_user_session', JSON.stringify(next));
      return next;
    });
  };

  const handleLogin = (loggedInUser: User) => {
    setUser(loggedInUser);
    localStorage.setItem('paklegal_user_session', JSON.stringify(loggedInUser));
    if (loggedInUser.role === 'Super Admin' || loggedInUser.role === 'Junior Admin') {
      navigate('/admin');
    } else if (loggedInUser.role === 'Client') {
      navigate('/search');
    } else if (loggedInUser.role === 'Lawyer') {
      navigate('/advocate-dashboard');
    }
  };

  const handleLogout = async () => {
    setUser(null);
    localStorage.removeItem('paklegal_user_session');
    try {
      const { auth } = await import('./firebase');
      await auth.signOut();
    } catch (e) {
      console.error('Sign out error:', e);
    }
    navigate('/search');
  };

  // We map the old `currentView` enum to the react-router location path to maintain compatibility with `AppLayout`
  const currentViewMap: Record<string, any> = {
    '/': 'landing',
    '/search': 'search',
    '/admin': 'admin-dashboard',
    '/login': 'login',
    '/dashboard': 'dashboard',
    '/advocate-dashboard': 'advocate-dashboard',
    '/workspace': 'workspace',
    '/repository': 'repository',
    '/advocates': 'advocates',
    '/advocate-marketplace': 'advocate-marketplace',
    '/client-portal': 'client-portal',
    '/my-cases': 'my-cases',
    '/laws': 'laws',
    '/blogs': 'blogs',
    '/ai-assistant': 'ai-assistant',
    '/about': 'about-me',
    '/about-me': 'about-me',
    '/privacy': 'privacy-policy',
    '/privacy-policy': 'privacy-policy',
    '/terms': 'terms-of-service',
    '/terms-of-service': 'terms-of-service',
    '/refund-cancellation-policy': 'refund-cancellation-policy',
    '/legal-disclaimer': 'legal-disclaimer',
    '/contact': 'contact',
  };
  const currentView = currentViewMap[location.pathname] || 'search';

  const handleNavigate = (view: any, customPath?: string) => {
    // Basic mapping from view string to path
    if (customPath) {
      navigate(customPath);
      return;
    }
    const pathMap: Record<string, string> = {
      'landing': '/',
      'search': '/search',
      'admin-dashboard': '/admin',
      'login': '/login',
      'dashboard': '/dashboard',
      'advocate-dashboard': '/advocate-dashboard',
      'workspace': '/workspace',
      'repository': '/repository',
      'advocates': '/advocates',
      'advocate-marketplace': '/advocate-marketplace',
      'client-portal': '/client-portal',
      'my-cases': '/my-cases',
      'laws': '/laws',
      'blogs': '/blogs',
      'ai-assistant': '/ai-assistant',
      'about-me': '/about',
      'about': '/about',
      'privacy-policy': '/privacy-policy',
      'privacy': '/privacy-policy',
      'terms-of-service': '/terms-of-service',
      'terms': '/terms-of-service',
      'refund-cancellation-policy': '/refund-cancellation-policy',
      'legal-disclaimer': '/legal-disclaimer',
      'contact': '/contact',
      'topic-hub': '/topics',
    };
    navigate(pathMap[view] || '/search');
  };

  const handleViewCase = (precedent: Precedent) => {
    navigate(`/precedents/${precedent.slug || precedent.id}`);
  };

  return (
    <AppLayout currentView={currentView} onNavigate={handleNavigate} user={user} onLogout={handleLogout} bookmarkedIds={bookmarkedIds}>
      <React.Suspense fallback={null}>
        {authInitialized && <Onboarding />}
      </React.Suspense>
      <React.Suspense fallback={<div className="h-full flex items-center justify-center bg-slate-50 p-6"><div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>}>
        <Routes>
          <Route path="/" element={<LandingView onGetStarted={() => handleNavigate('search')} onNavigate={handleNavigate} user={user} />} />
          <Route path="/search" element={<SearchView user={user} onViewCase={handleViewCase} bookmarkedIds={bookmarkedIds} onToggleBookmark={handleToggleBookmark} onNavigateToWorkspace={() => handleNavigate('workspace')} />} />
          <Route path="/login" element={<LoginView onLogin={handleLogin} />} />
          <Route 
            path="/admin" 
            element={
              !authInitialized ? (
                <div className="h-full flex items-center justify-center bg-slate-50 p-6">
                  <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (user?.role === 'Super Admin' || isSuperAdminEmail(user?.email)) ? (
                <AdminDashboardView />
              ) : (
                <Navigate to="/search" replace />
              )
            } 
          />
          <Route path="/dashboard" element={user?.role === 'Lawyer' ? <AdvocateDashboardView user={user!} onNavigate={handleNavigate} onUpdateUser={handleUpdateUser} /> : <DashboardView user={user!} onNavigate={handleNavigate} onViewCase={handleViewCase} />} />
          <Route path="/advocate-dashboard" element={<AdvocateDashboardView user={user!} onNavigate={handleNavigate} onUpdateUser={handleUpdateUser} />} />
          <Route path="/workspace" element={<WorkspaceView bookmarkedIds={bookmarkedIds} user={user!} onUpdateUser={handleUpdateUser} />} />
          <Route path="/repository" element={<RepositoryView bookmarkedIds={bookmarkedIds} onViewCase={handleViewCase} user={user} onUpdateUser={handleUpdateUser} />} />
          <Route path="/advocates" element={<AdvocatesView user={user} />} />
          <Route path="/advocate-marketplace" element={<AdvocateMarketplaceView user={user} onViewCase={handleViewCase} />} />
          <Route path="/client-portal" element={<ClientPortalView user={user!} onBack={() => handleNavigate('dashboard')} />} />
          <Route path="/my-cases" element={<MyCasesView user={user!} onNavigate={handleNavigate} />} />
          <Route path="/laws" element={<PublicLawsView onNavigate={handleNavigate} onViewLaw={(id) => handleNavigate('laws', `/laws/${id}`)} />} />
          <Route path="/laws/:id" element={<LawDetailPage handleNavigate={handleNavigate} />} />
          <Route path="/blogs" element={<BlogsView onNavigate={handleNavigate} onViewBlog={(id, slug) => handleNavigate('blogs', `/blogs/${slug || id}`)} />} />
          <Route path="/blogs/category/:category" element={<BlogsView onNavigate={handleNavigate} onViewBlog={(id, slug) => handleNavigate('blogs', `/blogs/${slug || id}`)} />} />
          <Route path="/blogs/tag/:tag" element={<BlogsView onNavigate={handleNavigate} onViewBlog={(id, slug) => handleNavigate('blogs', `/blogs/${slug || id}`)} />} />
          <Route path="/blogs/:lang/:id" element={<BlogDetailView user={user} onNavigate={handleNavigate} />} />
          <Route path="/blogs/:id" element={<BlogDetailView user={user} onNavigate={handleNavigate} />} />
          <Route path="/about" element={<AboutMeView user={user} />} />
          <Route path="/about-me" element={<AboutMeView user={user} />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyView />} />
          <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
          <Route path="/terms-of-service" element={<TermsOfServiceView />} />
          <Route path="/refund-cancellation-policy" element={<RefundCancellationPolicyView />} />
          <Route path="/disclaimer" element={<Navigate to="/legal-disclaimer" replace />} />
          <Route path="/legal-disclaimer" element={<LegalDisclaimerView />} />
          <Route path="/contact" element={<ContactView />} />
          <Route 
            path="/ai-assistant" 
            element={
              <AiAssistantView 
                user={user} 
                onViewCase={handleViewCase} 
                onViewLaw={(id) => handleNavigate('laws', `/laws/${id}`)}
                onViewAdvocate={(advocate) => {
                  const idOrSlug = typeof advocate === 'string' 
                    ? advocate 
                    : (advocate.id || advocate.userId || advocate.slug || advocate.displayName || advocate.name);
                  handleNavigate('advocate-marketplace', `/advocate-marketplace?advocateId=${encodeURIComponent(idOrSlug)}`);
                }}
              />
            } 
          />
          <Route path="/precedents/:id" element={<CaseReportPage user={user} bookmarkedIds={bookmarkedIds} handleToggleBookmark={handleToggleBookmark} handleNavigate={handleNavigate} />} />
          <Route path="/topics/:slug" element={<TopicHubPage handleNavigate={handleNavigate} handleViewCase={handleViewCase} />} />
          <Route path="*" element={<SearchView user={user} onViewCase={handleViewCase} bookmarkedIds={bookmarkedIds} onToggleBookmark={handleToggleBookmark} onNavigateToWorkspace={() => handleNavigate('workspace')} />} />
        </Routes>
      </React.Suspense>
    </AppLayout>
  );
}
